<template>
  <!-- 背景黑色蒙版 -->
  <div class="background-mask" v-if="isShowUploadDialog">
    <!-- 弹窗容器 -->
    <div class="dialog-container">
      <!-- 主标题 -->
      <div class="main-title">{{ dialogTitle }}</div>

      <!-- 副标题 -->
      <div
        v-if="uploadDialogSceneType === DialogSceneEnum.TEMP_FILE"
        class="login-guide-button"
        @click="clickLoginGuide"
      >
        <IconWechat class="icon-wechat"></IconWechat>
        <span>登录获取永久云链</span>
      </div>
      <div v-else class="sub-title">{{ dialogSubtitle }}</div>

      <!-- 链接列表容器 -->
      <div class="link-list-container">
        <LinkItem
          v-for="(item, index) in uploadedFileList"
          :key="index"
          :name="item.name"
          :url="item.url"
        ></LinkItem>
      </div>

      <!-- 根据场景显示不同的按钮 -->
      <div class="copy-button" @click="clickCopyAllUrl()">
        <IconLink class="icon-link"></IconLink>
        复制全部链接
      </div>
      <div class="download-qrcode-button" @click="clickDownloadAllQRCode()">
        <IconQRCode class="icon-qr-code"></IconQRCode>
        下载全部二维码
      </div>
    </div>

    <!-- 关闭图标 -->
    <IconCloseDialog
      class="icon-close-dialog"
      fillColor="#ffffff"
      @click="closeDialog"
    ></IconCloseDialog>
  </div>
</template>

<script setup>
import IconCloseDialog from "@/components/icons/IconCloseDialog.vue";
import IconLink from "@/components/icons/IconLink.vue";
import IconQRCode from "@/components/icons/IconQRCode.vue";
import IconWechat from "@/components/icons/IconWechat.vue";

import LinkItem from "./LinkItem.vue";
import Tracking from "@/model/tracking.js";
import { useUploadDialog } from "@/composables";
import { watch } from "vue";

const {
  state: {
    // 是否显示上传弹窗
    isShowUploadDialog,
    // 上传成功链接数据
    uploadedFileList,
    // 弹窗标题
    dialogTitle,
    // 弹窗副标题
    dialogSubtitle,
    // 是否显示操作按钮
    // showActionButtons,
    // 弹窗场景类型
    DialogSceneEnum,
    uploadDialogSceneType,
  },
  action: {
    // 复制全部 URL
    copyAllUrl,
    // 关闭上传弹窗
    closeUploadDialog,
    // 下载所有二维码
    downloadAllQRCode,
    // 点击登录按钮
    handleLoginClick,
  },
} = useUploadDialog();

// 点击关闭按钮
const closeDialog = () => {
  Tracking.send({
    trigger: "button-click",
    page: "main-page",
    dialog: "upload-successful-dialog",
    object: "close-btn",
    scene:
      uploadDialogSceneType.value === DialogSceneEnum.PERMANENT_FILE
        ? "permanent"
        : "temporary",
  });
  closeUploadDialog();
};

// 复制全部Url
const clickCopyAllUrl = () => {
  Tracking.send({
    trigger: "button-click",
    page: "main-page",
    dialog: "upload-successful-dialog",
    object: "copy-all-link-btn",
    scene:
      uploadDialogSceneType.value === DialogSceneEnum.PERMANENT_FILE
        ? "permanent"
        : "temporary",
  });
  copyAllUrl();
};

// 下载全部二维码
const clickDownloadAllQRCode = () => {
  Tracking.send({
    trigger: "button-click",
    page: "main-page",
    dialog: "upload-successful-dialog",
    object: "download-all-qrcode-btn",
    scene:
      uploadDialogSceneType.value === DialogSceneEnum.PERMANENT_FILE
        ? "permanent"
        : "temporary",
  });
  downloadAllQRCode();
};

// 监听弹窗显示状态，在实际显示时才发送曝光埋点
watch(isShowUploadDialog, (newVal) => {
  if (newVal) {
    Tracking.send({
      trigger: "dialog-view",
      page: "main-page",
      dialog: "upload-successful-dialog",
      scene:
        uploadDialogSceneType.value === DialogSceneEnum.PERMANENT_FILE
          ? "permanent"
          : "temporary",
    });
  }
});

const clickLoginGuide = () => {
  Tracking.send({
    trigger: "button-click",
    page: "main-page",
    dialog: "upload-successful-dialog",
    object: "login-guide-btn",
  });
  handleLoginClick();
};
</script>

<style lang="scss" scoped>
.background-mask {
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  width: 100%;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10;

  .dialog-container {
    width: min(px2rem(360), 96vw);
    height: px2rem(554);
    background: #262626;
    border: px2rem(1) solid #79ca9e;
    border-radius: px2rem(11);
    display: flex;
    flex-direction: column;
    align-items: center;

    .main-title {
      font-size: px2rem(20);
      margin-top: px2rem(30);
      color: #ffffff;
      font-family: "思源黑体 Bold";
    }

    .sub-title {
      font-size: px2rem(14);
      margin-top: px2rem(10);
      color: #ffffff;
      font-family: "思源黑体 Regular";
      opacity: 0.5;
    }

    .login-guide-button {
      cursor: pointer;
      font-size: px2rem(14);
      margin-top: px2rem(10);
      margin-bottom: px2rem(2);
      color: rgba(255, 255, 255, 0.6);
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: "思源黑体 Light";
      transition: all 0.3s ease;
      padding: px2rem(4) px2rem(12);
      border-radius: px2rem(15);
      position: relative;
      overflow: hidden;

      &::after {
        content: "";
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          90deg,
          transparent,
          rgba(255, 255, 255, 0.12),
          rgba(255, 255, 255, 0.04),
          transparent
        );
        animation: shine 3.5s cubic-bezier(0.4, 0, 0.2, 1) infinite;
      }

      &:hover {
        color: rgba(255, 255, 255, 0.9);
        background: rgba(121, 202, 158, 0.1);

        .icon-wechat {
          opacity: 0.85;
          transform: scale(1.05);
        }
      }

      .icon-wechat {
        width: px2rem(14);
        height: px2rem(14);
        margin-right: px2rem(8);
        opacity: 0.6;
        transition: all 0.3s ease;
      }

      span {
        border-bottom: none;
        position: relative;

        &::after {
          content: "";
          position: absolute;
          bottom: px2rem(-2);
          left: 0;
          width: 100%;
          height: 1px;
          background: rgba(255, 255, 255, 0.3);
          transform: scaleX(0);
          transition: transform 0.3s ease;
          transform-origin: left;
        }
      }

      &:hover span::after {
        transform: scaleX(1);
      }
    }

    @keyframes shine {
      0% {
        left: -100%;
      }
      75% {
        left: 100%;
      }
      100% {
        left: 100%;
      }
    }

    .link-list-container {
      width: 100%;
      height: px2rem(312);
      margin-top: px2rem(15);
      margin-bottom: px2rem(10);
      display: flex;
      flex-direction: column;
      align-items: center;
      overflow-y: scroll;
    }

    .copy-button {
      cursor: pointer;
      width: px2rem(304);
      height: px2rem(44);
      border-radius: px2rem(22);
      background: #19725e;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: px2rem(15);
      color: #ffffff;
      font-family: "思源黑体 Medium";

      .icon-link {
        width: px2rem(12);
        height: px2rem(12);
        margin-right: px2rem(10);
      }
    }

    .download-qrcode-button {
      cursor: pointer;
      width: px2rem(304);
      height: px2rem(44);
      border-radius: px2rem(22);
      background: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: px2rem(15);
      color: #19725e;
      font-family: "思源黑体 Medium";
      margin-top: px2rem(10);

      .icon-qr-code {
        width: px2rem(12);
        height: px2rem(12);
        margin-right: px2rem(10);
      }
    }
  }

  .icon-close-dialog {
    cursor: pointer;
    width: px2rem(25);
    height: px2rem(25);
    margin-top: px2rem(20);
  }
}

// @media only screen and (orientation: landscape) {
//   .dialog-container {
//     position: relative;
//     width: 28.28vw;
//     height: 77.03vh;
//     background: #262626;
//     border-radius: 1.57vh;
//     box-shadow: 0 0.28vh 0.56vh rgba(0, 0, 0, 0.16);
//     display: flex;
//     flex-direction: column;
//     align-items: center;
//     .icon-laugh-face {
//       width: 3.7vh;
//       height: 2.56vh;
//       margin-top: 5.56vh;
//     }

//     .main-title {
//       font-size: 3.24vh;
//       margin-top: 4.19vh;
//       color: white;
//       font-family: "思源黑体 Bold";
//     }

//     .sub-title {
//       font-size: 2.04vh;
//       margin-top: 1.2vh;
//       color: white;
//       font-family: "思源黑体 Medium";
//     }

//     .link-list-container {
//       width: 100%;
//       height: 35.65vh;
//       margin-top: 3.7vh;
//       margin-bottom: 3.8vh;
//       display: flex;
//       flex-direction: column;
//       align-items: center;
//       overflow-y: scroll;
//     }

//     .copy-button {
//       width: 23.7vw;
//       height: 6.2vh;
//       border-radius: 3.1vh;
//       box-shadow: 0 0.28vh 0.56vh rgba(0, 0, 0, 0.16);
//       cursor: pointer;
//       background: #19725e;
//       display: flex;
//       align-items: center;
//       justify-content: center;
//       font-size: 2.04vh;
//       color: #ffffff;
//       font-family: "思源黑体 Medium";

//       .icon-link {
//         padding-top: 0.4vh;
//         margin-right: 0.63vw;
//         width: 2.22vh;
//         height: 2.22vh;
//       }
//     }

//     .download-qrcode-button {
//       width: 23.7vw;
//       height: 6.2vh;
//       border-radius: 3.1vh;
//       box-shadow: 0 0.28vh 0.56vh rgba(0, 0, 0, 0.16);
//       cursor: pointer;
//       background: #ffffff;
//       display: flex;
//       align-items: center;
//       justify-content: center;
//       font-size: 2.04vh;
//       color: #19725e;
//       font-family: "思源黑体 Medium";
//       margin-top: 1.2vh;
//       .icon-qr-code {
//         padding-top: 0.4vh;
//         margin-right: 0.63vw;
//         width: 2vh;
//         height: 2vh;
//       }
//     }

//     .icon-close-dialog {
//       position: absolute;
//       width: 3.7vh;
//       height: 3.7vh;
//       right: -1.85vh;
//       top: -1.85vh;
//       cursor: pointer;
//     }
//   }
// }

// @media only screen and (orientation: portrait) {
//   .dialog-container {
//     position: relative;
//     width: 92.45vw;
//     height: 65.58vh;
//     background: #262626;
//     border-radius: 1.57vh;
//     box-shadow: 0 0.28vh 0.56vh rgba(0, 0, 0, 0.16);
//     display: flex;
//     flex-direction: column;
//     align-items: center;
//     .icon-laugh-face {
//       width: 6.81vw;
//       height: 2.18vh;
//       margin-top: 4.73vh;
//     }

//     .main-title {
//       font-size: 2.69vh;
//       margin-top: 3.55vh;
//       color: white;
//       font-family: "思源黑体 Bold";
//     }

//     .link-list-container {
//       width: 100%;
//       height: 30.37vh;
//       margin-top: 3.33vh;
//       margin-bottom: 3.19vh;
//       display: flex;
//       flex-direction: column;
//       align-items: center;
//       overflow-y: scroll;
//     }

//     .copy-button {
//       width: 77.47vw;
//       height: 5.28vh;
//       border-radius: 3.1vh;
//       box-shadow: 0 0.28vh 0.56vh rgba(0, 0, 0, 0.16);
//       cursor: pointer;
//       background: #19725e;
//       display: flex;
//       align-items: center;
//       justify-content: center;
//       font-size: 1.67vh;
//       color: #ffffff;
//       font-family: "思源黑体 Medium";

//       .icon-link {
//         padding-top: 0.4vh;
//         margin-right: 1vw;
//         width: 2.22vh;
//         height: 2.22vh;
//       }
//     }

//     .download-qrcode-button {
//       width: 77.47vw;
//       height: 5.28vh;
//       border-radius: 3.1vh;
//       box-shadow: 0 0.28vh 0.56vh rgba(0, 0, 0, 0.16);
//       cursor: pointer;
//       background: #ffffff;
//       display: flex;
//       align-items: center;
//       justify-content: center;
//       font-size: 1.67vh;
//       color: #19725e;
//       font-family: "思源黑体 Medium";
//       margin-top: 1.2vh;

//       .icon-qr-code {
//         padding-top: 0.4vh;
//         margin-right: 1vw;
//         width: 2vh;
//         height: 2vh;
//       }
//     }

//     .icon-close-dialog {
//       position: absolute;
//       width: 3.15vh;
//       height: 3.15vh;
//       right: -1.575vh;
//       top: -1.575vh;
//       cursor: pointer;
//     }
//   }
// }

// .login-button {
//   width: 77.47vw;
//   height: 5.28vh;
//   border-radius: 3.1vh;
//   box-shadow: 0 0.28vh 0.56vh rgba(0, 0, 0, 0.16);
//   cursor: pointer;
//   background: #19725e;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   font-size: 1.67vh;
//   color: #ffffff;
//   font-family: "思源黑体 Medium";

//   .icon-wechat {
//     padding-top: 0.4vh;
//     margin-right: 1vw;
//     width: 2.22vh;
//     height: 2.22vh;
//   }
// }
</style>
